import DOMPurify from 'dompurify'
import { hasVisibleText } from '@assessments/shared'
import { cn } from '@/lib/utils'

interface RichTextProps {
  /** Authored rich text (HTML subset) or plain string; null/empty renders nothing. */
  html: string | null
  id?: string
  className?: string
}

// Shared renderer for all admin-authored rich text. Authored content is
// still treated as untrusted at render time: sanitized to this fixed tag subset.
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
]
const ALLOWED_ATTR = ['href', 'target', 'rel']

// Registered once at module load; applies to every sanitize call.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'A') return
  node.setAttribute('rel', 'noopener noreferrer')
  const href = node.getAttribute('href') ?? ''
  if (/^https?:\/\//i.test(href)) node.setAttribute('target', '_blank')
})

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
}

function isPlainText(clean: string): boolean {
  return !/<[a-z][\s\S]*>/i.test(clean)
}

// One shared style block so every scope renders the subset identically.
const subsetStyles = cn(
  'text-sm text-subtle [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
  '[&_h1]:mb-1 [&_h1]:mt-3 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:text-body',
  '[&_h2]:mb-1 [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-body',
  '[&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:font-semibold [&_h3]:text-body',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5',
  '[&_strong]:font-semibold [&_b]:font-semibold [&_em]:italic',
  '[&_a]:text-info [&_a]:underline [&_a]:underline-offset-2',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic',
)

/** Whether `html` shows any text once sanitized; markup-only content counts as empty. */
export function hasRichText(html: string | null): boolean {
  return Boolean(html && hasVisibleText(sanitize(html)))
}

export function RichText({ html, id, className }: RichTextProps) {
  if (!html || !html.trim()) return null

  let clean = sanitize(html)
  if (!clean.trim()) return null
  // A bare string (no markup) is valid input; present it as a single paragraph.
  if (isPlainText(clean)) clean = `<p>${clean}</p>`

  return (
    <div
      id={id}
      data-richtext
      className={cn(subsetStyles, className)}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  )
}
